import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { Button, buttonClasses } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Money } from '@/components/Money';
import { Sheet } from '@/components/Sheet';
import { useToast } from '@/components/Toast';
import type { Order } from './api';
import { useCustomer } from './context';
import {
  DraftBadge,
  ListSkeleton,
  Notice,
  OrderProgress,
  OrderStatusBadge,
  PageTitle,
  Panel,
  PartyLine,
  formatTime,
} from './parts';
import { useCancelOrder, useCart, useOrders } from './queries';
import { canRequestCancel, describeCustomerError, orderTitle } from './wording';

function itemCount(order: Order): string {
  const count = order.items.length;
  return `${count} ${count === 1 ? 'item' : 'items'}`;
}

/** Placed orders, newest first, with the draft cart shown separately so the two are never confused. */
export function OrdersPage() {
  const orders = useOrders({ poll: true });
  const cart = useCart();
  const { money, session } = useCustomer();

  const draft = cart.data && cart.data.items.length > 0 ? cart.data : null;
  const draftCard = draft && (
    <Link
      to="/t/cart"
      className="mb-4 flex items-center justify-between gap-3 rounded-surface border border-dashed border-line-strong bg-white/60 px-4 py-3 text-ink no-underline hover:bg-white"
    >
      <span className="min-w-0">
        <span className="block text-base font-semibold">Your cart</span>
        <span className="block text-sm text-muted">
          {draft.total_quantity} {draft.total_quantity === 1 ? 'item' : 'items'} waiting to be
          ordered
        </span>
      </span>
      <DraftBadge />
    </Link>
  );

  const title = <PageTitle subtitle={<PartyLine />}>Orders</PageTitle>;

  if (orders.isPending) {
    return (
      <>
        {title}
        <ListSkeleton rows={3} />
      </>
    );
  }
  if (orders.isError && !orders.data) {
    return (
      <>
        {title}
        <ErrorState error={orders.error} onRetry={() => void orders.refetch()} />
      </>
    );
  }

  const list = [...(orders.data ?? [])].reverse();
  return (
    <>
      {title}
      {draftCard}
      {list.length === 0 ? (
        <EmptyState
          icon="orders"
          title="No orders yet"
          message="Orders you place will appear here."
          action={
            <Link to="/t/menu" className={buttonClasses({ variant: 'primary' })}>
              Browse the menu
            </Link>
          }
        />
      ) : (
        <Panel>
          <ul className="divide-y divide-line" aria-label="Placed orders">
            {list.map((order) => (
              <li key={order.order_id}>
                <Link
                  to={`/t/orders/${order.order_id}`}
                  className="flex items-center gap-3 px-4 py-3 text-ink no-underline hover:bg-surface"
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-semibold">
                        {orderTitle(order.order_number)}
                      </span>
                      <OrderStatusBadge status={order.status} locationType={session.locationType} />
                    </span>
                    <span className="text-sm text-muted">
                      {itemCount(order)} · {formatTime(order.created_at)} ·{' '}
                      <Money value={money(order.total_amount)} className="font-semibold text-ink" />
                    </span>
                  </span>
                  <Icon name="chevronRight" size={18} className="shrink-0 text-faint" />
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </>
  );
}

/** One placed order. The backend has no single-order endpoint, so this reads it from the order list. */
export function OrderDetailPage() {
  const { orderId = '' } = useParams();
  const location = useLocation();
  const orders = useOrders({ poll: true });
  const { money, session } = useCustomer();
  const cancel = useCancelOrder();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const justPlaced = (location.state as { justPlaced?: boolean } | null)?.justPlaced === true;

  const back = (
    <Link
      to="/t/orders"
      className="mb-2 inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-primary no-underline"
    >
      <Icon name="chevronLeft" size={18} />
      All orders
    </Link>
  );

  if (orders.isPending) {
    return (
      <>
        {back}
        <ListSkeleton rows={3} />
      </>
    );
  }
  if (orders.isError && !orders.data) {
    return (
      <>
        {back}
        <ErrorState error={orders.error} onRetry={() => void orders.refetch()} />
      </>
    );
  }

  const order = orders.data?.find((candidate) => candidate.order_id === orderId);
  if (!order) {
    return (
      <>
        {back}
        <EmptyState
          icon="orders"
          title="Order not found"
          message="We couldn't find that order."
          action={
            <Link to="/t/orders" className={buttonClasses({ variant: 'secondary' })}>
              See all orders
            </Link>
          }
        />
      </>
    );
  }

  const title = orderTitle(order.order_number);
  const cancelled = order.status === 'CANCELLED';
  const failure = cancel.isError ? describeCustomerError(cancel.error, session.locationType) : null;

  function confirmCancel() {
    cancel.mutate(orderId, {
      onSuccess: () => toast.show(`${title} cancelled`, { tone: 'success' }),
      onSettled: () => setConfirming(false),
    });
  }

  return (
    <>
      {back}
      <PageTitle
        aside={<OrderStatusBadge status={order.status} locationType={session.locationType} />}
      >
        {title}
      </PageTitle>
      <div className="flex flex-col gap-4">
        {/* The "placed" note lasts only until the kitchen picks the order up; then the progress says it all. */}
        {justPlaced && order.status === 'SUBMITTED' && !failure && (
          <Notice tone="success">Order placed. We'll keep this page up to date.</Notice>
        )}
        {failure && (
          <Notice tone="error">
            <strong className="block">{failure.title}</strong>
            {failure.message}
          </Notice>
        )}

        {cancelled ? (
          <Notice tone="info">
            <strong className="block">This order was cancelled</strong>
            Placed at {formatTime(order.created_at)}. Nothing from it goes on your bill.
          </Notice>
        ) : (
          <Panel className="px-4 pt-4 pb-3">
            <OrderProgress status={order.status} locationType={session.locationType} />
            <p className="mt-3 text-center text-xs text-faint">
              Placed at {formatTime(order.created_at)}
            </p>
          </Panel>
        )}

        <Panel>
          <ul className="divide-y divide-line" aria-label="Items in this order">
            {order.items.map((item) => (
              <li
                key={item.order_item_id}
                className="flex items-start justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-base font-semibold text-ink">
                    {item.name}{' '}
                    <span className="font-medium text-muted tabular-nums">×{item.quantity}</span>
                  </p>
                  {item.modifiers.length > 0 && (
                    <p className="text-sm text-muted">{item.modifiers.join(', ')}</p>
                  )}
                </div>
                <OrderStatusBadge
                  status={item.fulfillment_status}
                  locationType={session.locationType}
                />
              </li>
            ))}
          </ul>
          {!cancelled && (
            <p className="flex justify-between border-t border-line px-4 py-3 text-base font-bold text-ink">
              <span>Order total</span>
              <Money value={money(order.total_amount)} />
            </p>
          )}
        </Panel>

        {!cancelled && (
          <p className="text-sm text-muted">
            Your running bill is on the{' '}
            <Link to="/t/bill" className="font-semibold text-primary">
              Bill
            </Link>{' '}
            tab.
          </p>
        )}

        {canRequestCancel(order.status) && (
          <Button variant="destructive" fullWidth onClick={() => setConfirming(true)}>
            Cancel order
          </Button>
        )}
      </div>

      <Sheet open={confirming} onClose={() => setConfirming(false)} title={`Cancel ${title}?`}>
        <p className="text-base text-ink">The whole order will be cancelled.</p>
        <div className="mt-5 flex flex-col gap-2">
          <Button
            variant="destructive"
            size="lg"
            fullWidth
            loading={cancel.isPending}
            onClick={confirmCancel}
          >
            Yes, cancel order
          </Button>
          <Button variant="secondary" fullWidth onClick={() => setConfirming(false)}>
            Keep order
          </Button>
        </div>
      </Sheet>
    </>
  );
}
